import React, { useEffect, useState } from "react"
import { Calendar as RBCalendar, luxonLocalizer } from "react-big-calendar"
import { DateTime, Settings } from "luxon"
import "react-big-calendar/lib/css/react-big-calendar.css"
import {
  defaultBigCalendarMonthHeader,
  defaultDayPropGetter,
  defaultEventPropGetter,
  defaultGroupEventsByOverlap,
  formatVacationEventsTemplates,
  formatVacationEventTemplate,
  splitEventOnWeekend,
  splitEventSkippingWeekends,
} from "../../../utils/bigCalendar.utils"
import {
  BIG_CALENDAR_VIEWS,
  DEFAULT_BIG_CALENDAR_MESSAGES,
  DEFAULT_BIG_VALENDAR_VIEWS,
} from "../../../consts/bigCalendar.consts"
import { getVacationRequestPriority } from "../../../utils/vacation.utils"
import CalendarEventWrapper from "./CalendarEventWrapper.compound"
import useThemeContext from "../../../hooks/contextConsumers/useThemeContext.hook"

Settings.defaultLocale = "es"
const customLocalizer = luxonLocalizer(DateTime)
customLocalizer.sortEvents = (events) => {} // no-op to avoid weird default sorting by rbc
const BigCalendar = ({
  showOnlyEvent = null,
  messages = DEFAULT_BIG_CALENDAR_MESSAGES,
  views = DEFAULT_BIG_VALENDAR_VIEWS,
  className,
  onView = () => {},
  handleEventClick = () => {},
  handleNavigate = () => {},
  events = [],
  skipWeekends = true,
  groupEvents = true,
  formatEventsTemplates = formatVacationEventsTemplates || ((events) => events),
  loading = false,
  setGroupedEvents = () => {},
  localizer = customLocalizer,
  customGrouper = defaultGroupEventsByOverlap,
  style = {},
  view,
  dayPropGetter = defaultDayPropGetter,
  eventPropGetter = defaultEventPropGetter,
  tooltipAccessor = (e) => e.title,
  min,
  max,
  date = new Date(),
  components = {},
}) => {
  const { theme } = useThemeContext()
  const [currentView, setCurrentView] = useState(BIG_CALENDAR_VIEWS.MONTH)
  const [formattedEvents, setFormattedEvents] = useState([])
  useEffect(() => {
    const eventsSkippingWeekends =
      skipWeekends ? splitEventSkippingWeekends(events) : events
    const groupedEvents =
      groupEvents ?
        customGrouper(eventsSkippingWeekends)
      : eventsSkippingWeekends
    const formattedGroupEvents = formatEventsTemplates(groupedEvents, theme)
    const formattedEventsSkippingWeekends = formatEventsTemplates(
      eventsSkippingWeekends,
      theme
    )
    const finalEvents = (
      currentView === BIG_CALENDAR_VIEWS.MONTH ?
        formattedGroupEvents
      : formattedEventsSkippingWeekends)

      .sort((a, b) => {
        const aStart = new Date(a.start).getTime()
        const bStart = new Date(b.start).getTime()
        return aStart - bStart
      })
      ?.map((event, index) => ({
        ...event,
        priority: getVacationRequestPriority(event),
        start: DateTime.fromJSDate(event.start)
          .plus({ minutes: getVacationRequestPriority(event) })
          .toJSDate(),
      }))
      .sort((a, b) => a.priority - b.priority)
      .map((event, index) => ({
        ...event,
        id: index,
      }))
    setFormattedEvents(finalEvents)
    setGroupedEvents(finalEvents)
  }, [events, currentView, theme])
  const onNavigate = (date) => {
    const newDate = new Date(date)
    if (min && newDate < min) {
      return
    }
    if (max && newDate > max) {
      return
    }
    handleNavigate(date)
  }

  let ab = formattedEvents
  let newEvents = []

  if (showOnlyEvent) {
    for (let i = 0; i < ab.length; i++) {
      const event = ab[i]
      if (event.groupedEvents) {
        const foundEvent = event.groupedEvents.find((groupedEvent) => {
          return groupedEvent.vacationId === showOnlyEvent.vacationId
        })
        if (foundEvent) {
          newEvents = splitEventOnWeekend({
            ...foundEvent,
            start: DateTime.fromISO(foundEvent.startDate)
              .plus({ day: 1 })
              .toJSDate(),
            end: DateTime.fromISO(foundEvent.endDate)
              .plus({ day: 1 })
              .toJSDate(),
          }).map(formatVacationEventTemplate)
        }
      } else {
        const foundEvent = event.vacationId === showOnlyEvent.vacationId
        if (foundEvent) {
          newEvents.push(event)
        }
      }
    }
  }

  ab = newEvents.length ? newEvents : ab

  return (
    <RBCalendar
      className={`${loading ? "loading" : ""} ${className}`}
      localizer={localizer}
      events={ab}
      startAccessor="start"
      endAccessor="end"
      defaultView={BIG_CALENDAR_VIEWS.MONTH}
      onView={(e) => {
        setCurrentView(e)
        onView(e)
      }}
      view={view ?? currentView}
      views={views}
      messages={messages}
      date={date}
      popup
      style={{ height: "100%", ...style }}
      eventPropGetter={(e) => eventPropGetter(e, date, currentView)}
      onNavigate={onNavigate}
      onSelectEvent={handleEventClick}
      components={{
        month: {
          dateHeader: defaultBigCalendarMonthHeader,
        },
        eventWrapper: ({ event, children }) => (
          <CalendarEventWrapper event={event}>{children}</CalendarEventWrapper>
        ),
        ...components,
      }}
      formatEventsTemplates={formatEventsTemplates}
      tooltipAccessor={tooltipAccessor}
      min={min}
      max={max}
    />
  )
}

export default BigCalendar
